import React, { useState } from 'react'
import styled from 'styled-components/macro'
import { Text } from '@resultadosdigitais/tangram-components'
import { useTranslation } from 'react-i18next'
import { ChevronLeft } from '@resultadosdigitais/tangram-react-icons'

import Alert from '../Alert'
import Link from '../Link'
import Button from '../Button'
import Input from '../Input'
import InputLabel from '../InputLabel'
import Form from '../Form'
import FormControl from '../FormControl'
import loginService from '../../login-service'
import { validate, isEmail, isEmpty } from '../../validation'
import { isEmptyObject } from '../../object'

const StyledLink = styled(Link)`
  font-size: var(--text-size-md);
`

const StyledForm = styled(Form)`
  width: 440px;
`

function RecoverPassword({ onBackButtonClick, onSuccess }) {
  const { t } = useTranslation()

  const [formState, setFormState] = useState('idle')
  const [email, setEmail] = useState(emailByParam() ?? '')
  const [validation, setValidation] = useState()

  const schema = {
    email: [
      {
        method: isEmpty,
        validWhen: false,
        message: t('LoginForm.EmptyField'),
      },
      {
        method: isEmail,
        validWhen: true,
        message: t('LoginForm.InvalidEmailField'),
      },
    ],
  }

  const isSubmiting = formState === 'submiting'
  const isError = formState === 'error'

  function handleBackButtonClick(event) {
    event.preventDefault()
    onBackButtonClick()
  }

  function handleEmailChange(event) {
    event.preventDefault()

    if (isSubmiting) return

    setEmail(event.target.value)

    if (validation?.email) setValidation(validate({ email: event.target.value }, schema))
  }

  function handleFormSubmit(event) {
    event.preventDefault()

    if (isSubmiting) return

    const validation = validate({ email }, schema)
    setValidation(validation)

    if (isEmptyObject(validation)) submitForm()
  }

  async function submitForm() {
    setFormState('submiting')

    try {
      await loginService.recoverPassword({ email })
      onSuccess()
    } catch (e) {
      console.error(e)
      setFormState('error')
    }
  }

  return (
    <StyledForm onSubmit={handleFormSubmit} noValidate disabled={isSubmiting}>
      <StyledLink
        startIcon={<ChevronLeft title={t('PasswordRecoverForm.BackIcon')} titleId="backIcon" />}
        href="#"
        onClick={handleBackButtonClick}
        removeUnderline
      >
        {t('PasswordRecoverForm.BackButtonLabel')}
      </StyledLink>

      <Text token={Text.tokens.TEXT_XXL_BOLD}>{t('PasswordRecoverForm.Title')}</Text>

      <Text token={Text.tokens.FONT_SIZE_MD_SEMIBOLD} textColor="--color-gray-70">
        {t('PasswordRecoverForm.Description')}
      </Text>

      {isError && <Alert severity="error">{t('PasswordRecoverForm.DefaultError')}</Alert>}

      <FormControl errorMessage={validation?.email} disabled={isSubmiting}>
        <InputLabel htmlFor="email">{t('LoginForm.Email')}</InputLabel>
        <Input
          type="email"
          id="email"
          name="email"
          data-testid="recover-password-email"
          autoComplete="username"
          value={email}
          onChange={handleEmailChange}
          readOnly={isSubmiting}
        />
      </FormControl>

      <Button type="submit" loading={isSubmiting} data-testid="recover-password-submit">
        {t('PasswordRecoverForm.ConfirmButton')}
      </Button>
    </StyledForm>
  )
}

function emailByParam() {
  const urlParams = new URLSearchParams(window.location.search)
  const email = urlParams.get('email')

  return isEmail(email) ? email : ''
}

export default RecoverPassword
